import { ApolloClient, ApolloQueryResult } from '@apollo/client'

import {
  FinanceFactSummariesDocument,
  FinanceFactSummariesQuery,
  FinancialWorkspaceValueObjectsCostTypeEnum,
  FinancialWorkspaceValueObjectsFinanceTypeEnum,
} from 'generated/resources/graphql'

import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import SafeJSON from 'modules/core/lib/SafeJSON'

import { InsightTableGroupBy } from 'modules/finance/components/FinancialWorkspace/types/table'
import { FinancialWorkspaceRecord, ChildrenPaginationInfo } from 'modules/finance/components/FinancialWorkspaceDataCollection/types'

import { buildComparisonDataMap, transformItemToRecord } from 'modules/finance/components/FinancialWorkspace/components/Table/hooks/transformRecords'
import {
  FINANCE_TYPE_IDS,
  getResourceFilterKey,
  isExpensesRow,
} from 'modules/finance/components/FinancialWorkspace/components/Table/hooks/expanderHelpers'
import { mergeComparisonDataByIds } from 'modules/finance/components/FinancialWorkspaceDataCollection/utils/transformData'

interface UseFetchChildrenOptions {
  search?: string
  formattedEndDate: string
  formattedStartDate: string
  hasComparisonDate: boolean
  groupBy: InsightTableGroupBy
  client: ApolloClient<object>
  formattedCompareToEndDate?: string
  formattedCompareToStartDate?: string
  effectiveCostType: FinancialWorkspaceValueObjectsCostTypeEnum
  // Serialized filter keys for stable dependency tracking
  locationIdsKey: string
  employeeIdsKey: string
  referenceContractTreeLevelUuidsKey: string
  financeTypesKey: string
  costCenterIdsKey: string
  legalEntityIdsKey: string
}

/**
 * Hook that returns a callback to fetch children for FinanceFactSummaries (Employee Costs / Compensations)
 *
 * For non-Total views: Uses groupBy: Total with resource filter to get breakdown
 * For Total view:
 *   - Expenses: Uses groupBy: CustomCategory to get expense category breakdown
 *   - Payroll/Compensation: Uses groupBy: LineItem to get line item breakdown
 */
export const useFetchChildrenForFinanceFactSummaries = ({
  search,
  client,
  groupBy,
  locationIdsKey,
  employeeIdsKey,
  financeTypesKey,
  formattedEndDate,
  costCenterIdsKey,
  effectiveCostType,
  legalEntityIdsKey,
  hasComparisonDate,
  formattedStartDate,
  formattedCompareToEndDate,
  formattedCompareToStartDate,
  referenceContractTreeLevelUuidsKey
}: UseFetchChildrenOptions) => {
  return useUnsafeCallback(
    async (item: FinancialWorkspaceRecord, pagination?: ChildrenPaginationInfo): Promise<{ children: FinancialWorkspaceRecord[]; paginationInfo?: ChildrenPaginationInfo }> => {
      const perPage = 100
      // If children are already pre-loaded, return them directly
      if (item.children && item.children.length > 0) {
        return { children: item.children }
      }

      // Determine the filter key based on current groupBy
      const resourceFilterKey = getResourceFilterKey(groupBy)

      // Parse the stable keys back to arrays for the query
      const legalEntityIds = SafeJSON.parse<number[]>(legalEntityIdsKey, [])
      const locationIds = SafeJSON.parse<number[]>(locationIdsKey, [])
      const costCenterIds = SafeJSON.parse<number[]>(costCenterIdsKey, [])
      const employeeIds = SafeJSON.parse<number[]>(employeeIdsKey, [])
      const referenceContractTreeLevelUuids = SafeJSON.parse<string[]>(
        referenceContractTreeLevelUuidsKey,
        []
      )
      const financeTypesArray = SafeJSON.parse<FinancialWorkspaceValueObjectsFinanceTypeEnum[]>(
        financeTypesKey,
        []
      )

      // Determine the groupBy for children based on current view and item type
      let childrenGroupBy: InsightTableGroupBy
      let childFinanceTypes: FinancialWorkspaceValueObjectsFinanceTypeEnum[]

      // Check if the item being expanded is an Expenses row
      // This can happen in two scenarios:
      // 1. Top-level Total view with Expenses row
      // 2. Non-Total view where we expanded a resource and got Expenses as a child
      if (isExpensesRow(item)) {
        // Expenses rows expand to show CustomCategory breakdown
        childrenGroupBy = InsightTableGroupBy.CustomCategory
        childFinanceTypes = ['expense']
      } else if (groupBy === InsightTableGroupBy.Total) {
        // For Total view with non-Expenses rows, they shouldn't be expandable
        // (Payroll/Agreements don't expand in Total view)
        return { children: [] }
      } else {
        // For non-Total views (Cost Center, Employee, etc.)
        // Fetch with groupBy: Total to get Payroll/Expenses/Agreements breakdown for this resource
        childrenGroupBy = InsightTableGroupBy.Total
        childFinanceTypes = financeTypesArray
      }

      // Build variables for children query
      const emptyNumberArray: number[] = []
      const variables = {
        locationIds,
        employeeIds,
        costCenterIds,
        legalEntityIds,
        customCategoryIds: [],
        itemsPerPage: perPage,
        groupBy: childrenGroupBy,
        excludeChildrenData: true, // Children are leaf nodes, no further expansion
        search: search ?? undefined,
        costType: effectiveCostType,
        managerIds: emptyNumberArray,
        referenceContractTreeLevelUuids,
        financeTypes: childFinanceTypes,
      }

      // Override the filter to scope to the parent item (for non-Total views)
      if (resourceFilterKey) {
        variables[resourceFilterKey] = [Number(item.resourceId)]
      }

      const childrenVariables = {
        ...variables,
        formattedEndDate,
        formattedStartDate,
        after: item.childrenCursor
      }

      try {
        // Determine if these children are leaf nodes or can be further expanded
        // - When expanding an Expenses row: children are CustomCategory results (leaf nodes)
        // - When expanding a resource row: children are finance type rows (Expenses can expand)
        const isLeafLevel = isExpensesRow(item)

        const result = await client.query<FinanceFactSummariesQuery>({
          query: FinanceFactSummariesDocument,
          variables: childrenVariables,
          fetchPolicy: 'network-only',
        })

        let comparisonResult: ApolloQueryResult<FinanceFactSummariesQuery> | undefined = undefined

        if (hasComparisonDate) {
          const childrenComparisonVariables = {
            ...variables,
            after: item.comparisonChildrenCursor,
            formattedEndDate: formattedCompareToEndDate,
            formattedStartDate: formattedCompareToStartDate,
          }

          comparisonResult = await client.query<FinanceFactSummariesQuery>({
            query: FinanceFactSummariesDocument,
            variables: childrenComparisonVariables,
            fetchPolicy: 'network-only',
          })
        }

        const edges = result.data.financialWorkspace.financeFactSummariesConnection.edges

        let children =  edges.map((edge) => {
          // For non-leaf levels, Expenses children can be expanded further
          const childCanExpand = !isLeafLevel && edge.node.id === FINANCE_TYPE_IDS.EXPENSES
          const childId = `${item.id}::${edge.node.id}`

          const child = transformItemToRecord({
            id: childId,
            isChild: true,
            rows: edge.node.rows,
            level: item.level + 1,
            label: edge.node.label,
            active: edge.node.active,
            hasChildren: childCanExpand,
            resourceId: item.resourceId, // Inherit the resourceId from parent row
            imageUrl: edge.node.imageUrl,
            category: edge.node.category,
            categoryLabel: edge.node.categoryLabel,
          })

          return child
        })

        const comparisonData = buildComparisonDataMap(comparisonResult?.data, item.level + 1, item.id)
        const comparisonRecords = Object.values(comparisonData ?? {}).filter(Boolean)

        if (comparisonRecords.length > 0) {
          children = mergeComparisonDataByIds(children, comparisonRecords)
        }

        const pageInfo = result.data.financialWorkspace.financeFactSummariesConnection.pageInfo
        const comparisonPageInfo = comparisonResult?.data?.financialWorkspace?.financeFactSummariesConnection?.pageInfo
        const total = result.data.financialWorkspace.financeFactSummariesConnection.totalCount ?? 0

        item.childrenCursor = pageInfo.endCursor ?? undefined
        item.comparisonChildrenCursor = comparisonPageInfo?.endCursor ?? undefined

        const paginationInfo: ChildrenPaginationInfo = {
          total,
          perPage: perPage,
          hasMore: pageInfo.hasNextPage,
          pagesCount: Math.ceil(total / perPage),
          currentPage: (pagination?.currentPage ?? 0) + 1,
        }

        return { children, paginationInfo}
      } catch {
        return { children: [] }
      }
    },
    [
      search,
      client,
      groupBy,
      employeeIdsKey,
      locationIdsKey,
      financeTypesKey,
      formattedEndDate,
      costCenterIdsKey,
      hasComparisonDate,
      effectiveCostType,
      legalEntityIdsKey,
      formattedStartDate,
      formattedCompareToEndDate,
      formattedCompareToStartDate,
      referenceContractTreeLevelUuidsKey
    ]
  )
}